/**
 * Browser-only wishlist: stores product handles in localStorage and keeps every heart on
 * the page for the same product in sync. Storage may be unavailable (private mode); the
 * hearts then still toggle for the current page view.
 */
const STORAGE_KEY = 'eb-wishlist';
const CHANGE_EVENT = 'eb-wishlist:change';

/** @returns {string[]} */
function readList() {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} list */
function writeList(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage blocked: keep the in-page state only.
  }
}

let memoryList = readList();

class EbWishlistButton extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.button?.addEventListener('click', () => this.toggle(), { signal });
    document.addEventListener(CHANGE_EVENT, () => this.#render(), { signal });
    window.addEventListener(
      'storage',
      (event) => {
        if (event.key !== STORAGE_KEY) return;
        memoryList = readList();
        this.#render();
      },
      { signal }
    );
    this.#render();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get button() {
    return this.querySelector('button');
  }

  get handle() {
    return this.dataset.handle || '';
  }

  toggle() {
    if (!this.handle) return;
    const saved = memoryList.includes(this.handle);
    memoryList = saved ? memoryList.filter((item) => item !== this.handle) : [...memoryList, this.handle];
    writeList(memoryList);
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handle: this.handle, saved: !saved } }));
  }

  #render() {
    const button = this.button;
    if (!button) return;
    const saved = memoryList.includes(this.handle);
    button.setAttribute('aria-pressed', String(saved));
    if (button.hasAttribute('aria-label')) {
      const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    }
  }
}

if (!customElements.get('eb-wishlist-button')) customElements.define('eb-wishlist-button', EbWishlistButton);
